import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Pagination,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchTransactionsThunk } from "../../../services/MasterApi";

export default function Transactions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const transactionDetails = useSelector((state) => state.transationsData);
  const { data: rawTableData, pagination, isLoading } = transactionDetails;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("ALL"); // ALL, PAID, PENDING

  const token = sessionStorage.getItem("bb_token");
  let userId = sessionStorage.getItem("bb_userId");
  const role = sessionStorage.getItem("bb_role");
  if (role === "admin") userId = undefined;

  const loadData = useCallback(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    const params = {
      token,
      page,
      size: rowsPerPage,
      searchParameter: searchTerm.trim(),
      sortDirection: "DESC",
      userId: userId || "",
    };
    dispatch(fetchTransactionsThunk(params));
  }, [dispatch, token, page, rowsPerPage, searchTerm, userId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (pagination) {
      setTotalPages(pagination.totalPages || 1);
      if (pagination.currentPage !== undefined) {
        setPage(pagination.currentPage);
      }
    }
  }, [pagination]);

  // Client-side payment status filter if needed
  const filteredData = useMemo(() => {
    if (!rawTableData || !Array.isArray(rawTableData)) return [];
    if (paymentFilter === "ALL") return rawTableData;
    if (paymentFilter === "PAID") {
      return rawTableData.filter((row) => Boolean(row.businessPayment?.paymentTransactionId));
    }
    if (paymentFilter === "PENDING") {
      return rawTableData.filter((row) => !row.businessPayment?.paymentTransactionId);
    }
    return rawTableData;
  }, [rawTableData, paymentFilter]);

  // Metrics calculation
  const metrics = useMemo(() => {
    if (!rawTableData || !Array.isArray(rawTableData)) {
      return { totalRevenue: 0, paidCount: 0, pendingCount: 0 };
    }
    let totalRevenue = 0;
    let paidCount = 0;
    let pendingCount = 0;

    rawTableData.forEach((row) => {
      const isPaid = Boolean(row.businessPayment?.paymentTransactionId);
      if (isPaid) {
        paidCount++;
        const amt = parseFloat(row.businessPayment?.totalPrice || 0);
        if (!isNaN(amt)) totalRevenue += amt;
      } else {
        pendingCount++;
      }
    });

    return { totalRevenue, paidCount, pendingCount };
  }, [rawTableData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleRowClick = (row) => {
    const txId = row.businessTransactionId || row.transactionId;
    if (txId) {
      sessionStorage.setItem("bb_transactionId", txId);
      navigate(`/details/${txId}`);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f8fafc] py-6 px-4 sm:px-8 font-sans">
      <div className="w-full space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-2xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Settled Volume
              </span>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <i className="fa-solid fa-dollar-sign text-sm"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mt-2">
              ${metrics.totalRevenue.toFixed(2)}
            </h3>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              From current active ledger
            </span>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-2xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Completed Payments
              </span>
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#1a73e8] flex items-center justify-center">
                <i className="fa-solid fa-circle-check text-sm"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mt-2">
              {metrics.paidCount}
            </h3>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Verified Stripe transactions
            </span>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-2xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Pending Payments
              </span>
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <i className="fa-solid fa-clock-rotate-left text-sm"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mt-2">
              {metrics.pendingCount}
            </h3>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Unsettled invoices
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <i className="fa-solid fa-magnifying-glass text-xs"></i>
            </span>
            <input
              type="text"
              className="w-full pl-9 pr-8 py-2 bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8]"
              placeholder="Search by business, payer, or Stripe ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
              >
                <i className="fa-solid fa-xmark text-xs"></i>
              </button>
            )}
          </div>

          {/* Payment Status Segmented Control */}
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 p-1 rounded-lg border border-slate-200">
              <button
                type="button"
                onClick={() => setPaymentFilter("ALL")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  paymentFilter === "ALL"
                    ? "bg-white text-slate-900 shadow-2xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setPaymentFilter("PAID")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  paymentFilter === "PAID"
                    ? "bg-white text-emerald-700 shadow-2xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Paid
              </button>
              <button
                type="button"
                onClick={() => setPaymentFilter("PENDING")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  paymentFilter === "PENDING"
                    ? "bg-white text-amber-700 shadow-2xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Pending
              </button>
            </div>

            {/* Refresh */}
            <button
              type="button"
              onClick={loadData}
              disabled={isLoading}
              className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:text-[#1a73e8] transition-colors flex items-center gap-1.5 shadow-2xs"
            >
              <i className={`fa-solid fa-arrow-rotate-right text-xs ${isLoading ? "animate-spin text-[#1a73e8]" : ""}`}></i>
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <Paper
          className="table-shell"
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "14px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <TableContainer>
            {isLoading ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "45vh",
                }}
              >
                <CircularProgress size={32} sx={{ color: "#1a73e8" }} />
                <p style={{ color: "#64748b", marginTop: "1rem", fontSize: "13px" }}>
                  Loading financial transaction records...
                </p>
              </Box>
            ) : filteredData && filteredData.length > 0 ? (
              <Table aria-label="transactions-ledger-table">
                <TableHead sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <TableRow>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Payment Ref / Stripe ID
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      App ID & Business
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Payer / Applicant
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Amount
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Date & Time
                    </TableCell>
                    <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row, index) => {
                    const txId = row.businessTransactionId || row.transactionId;
                    const stripeId = row.businessPayment?.paymentTransactionId;
                    const isPaid = Boolean(stripeId);
                    const payerName =
                      row.businessPayment?.payer?.name ||
                      row.primaryContact?.fullName ||
                      row.ownerName ||
                      "N/A";
                    const payerEmail =
                      row.businessPayment?.payer?.email || row.primaryContact?.email;
                    const amount = row.businessPayment?.totalPrice
                      ? parseFloat(row.businessPayment.totalPrice).toFixed(2)
                      : "0.00";

                    return (
                      <TableRow
                        key={txId || index}
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background 0.15s ease",
                          "&:hover": { backgroundColor: "#f8fafc" },
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        {/* Stripe Ref */}
                        <TableCell align="left">
                          {stripeId ? (
                            <span className="font-mono text-xs font-semibold text-slate-800 bg-slate-100 px-2 py-1 rounded border border-slate-200 block max-w-[200px] truncate">
                              {stripeId}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              Unassigned (Pending)
                            </span>
                          )}
                        </TableCell>

                        {/* App ID & Business */}
                        <TableCell align="left">
                          <span className="font-semibold text-slate-900 text-[13px] block leading-snug">
                            {row.businessName || "Unnamed Business"}
                          </span>
                          <span className="text-[#1a73e8] font-semibold text-[11px]">
                            #REGD{txId}
                          </span>
                        </TableCell>

                        {/* Payer */}
                        <TableCell align="left">
                          <span className="font-medium text-slate-800 text-[13px] block leading-snug">
                            {payerName}
                          </span>
                          {payerEmail && (
                            <span className="text-[11px] text-slate-400 block">
                              {payerEmail}
                            </span>
                          )}
                        </TableCell>

                        {/* Amount */}
                        <TableCell align="left">
                          <span className="text-sm font-bold text-slate-900">
                            ${amount}
                          </span>
                          <span className="block text-[10px] text-slate-400 uppercase font-semibold">
                            {row.businessPayment?.paymentMode || "Stripe"}
                          </span>
                        </TableCell>

                        {/* Date */}
                        <TableCell align="left" sx={{ color: "#64748b", fontSize: "12px" }}>
                          {formatDate(row.createDate)}
                        </TableCell>

                        {/* Status */}
                        <TableCell align="center">
                          {isPaid ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <i className="fa-solid fa-circle-check text-[11px]"></i>
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              <i className="fa-solid fa-clock text-[11px]"></i>
                              Pending
                            </span>
                          )}
                        </TableCell>

                        {/* Action */}
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => handleRowClick(row)}
                            className="bg-blue-50 hover:bg-blue-100 text-[#1a73e8] border border-blue-100 rounded-lg px-2.5 py-1 text-xs font-semibold inline-flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <span>View</span>
                            <i className="fa-solid fa-arrow-right text-[10px]"></i>
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                  <i className="fa-solid fa-receipt text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  No payment transactions found
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mb-4">
                  {searchTerm || paymentFilter !== "ALL"
                    ? "No transactions matched your search or status filter."
                    : "No payment records have been processed in the system yet."}
                </p>
                {(searchTerm || paymentFilter !== "ALL") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setPaymentFilter("ALL");
                    }}
                    className="px-3.5 py-1.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            )}
          </TableContainer>

          {/* Pagination */}
          <div className="p-3 sm:p-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              Showing page <strong className="text-slate-700">{page + 1}</strong> of{" "}
              <strong className="text-slate-700">{totalPages}</strong> ({pagination?.totalRows || 0} total applications)
            </span>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page + 1}
                color="primary"
                onChange={(e, p) => setPage(p - 1)}
                size="small"
                sx={{
                  "& .Mui-selected": {
                    backgroundColor: "#1a73e8 !important",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          </div>
        </Paper>
      </div>
    </div>
  );
}
